// ErrorBoundary.jsx - Production-Safe Error Boundary
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
    
    // Bind methods
    this.handleRetry = this.handleRetry.bind(this);
    this.handleClearData = this.handleClearData.bind(this);
    this.handleReportError = this.handleReportError.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Report error in production
    this.reportError(error, errorInfo);
  }

  // Enhanced error reporting
  reportError(error, errorInfo) {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId: this.state.errorId,
      appVersion: process.env.REACT_APP_VERSION || '1.0.0',
      nodeEnv: process.env.NODE_ENV
    };

    // Development: Log detailed error
    if (isDevelopment) {
      console.group('Error Boundary Report');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.table(errorReport);
      console.groupEnd();
    }

    try {
      // Store error locally for debugging
      const errorHistory = JSON.parse(localStorage.getItem('tegim_error_history') || '[]');
      errorHistory.unshift(errorReport);
      
      // Keep only last 10 errors
      if (errorHistory.length > 10) {
        errorHistory.splice(10);
      }
      
      localStorage.setItem('tegim_error_history', JSON.stringify(errorHistory));
    } catch (storageError) {
      console.warn('Could not save error to localStorage:', storageError);
    }

    // Production: Send to error reporting service (if available)
    if (!isDevelopment && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: true,
        custom_map: { errorId: this.state.errorId }
      });
    }
  }

  // Retry mechanism
  handleRetry() {
    const newRetryCount = this.state.retryCount + 1;
    
    if (newRetryCount > 3) {
      // Too many retries, suggest data clear
      this.setState({ tooManyRetries: true });
      return;
    }

    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: newRetryCount
    });
  }

  // Clear all application data
  handleClearData() {
    try {
      // Clear localStorage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('tegim_') || key.startsWith('api_')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Reload page
      window.location.reload();
    } catch (error) {
      console.error('Error clearing data:', error);
      // Force reload if clearing fails
      window.location.href = window.location.pathname;
    }
  }

  // Report error to external service
  handleReportError() {
    const errorData = {
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString()
    };

    // Copy error details to clipboard
    navigator.clipboard.writeText(JSON.stringify(errorData, null, 2))
      .then(() => {
        alert('Hata detayları panoya kopyalandı. Destek ekibiyle paylaşabilirsiniz.');
      })
      .catch(() => {
        // Fallback: Show error in alert
        alert(`Hata ID: ${this.state.errorId}\nLütfen bu ID'yi destek ekibiyle paylaşın.`);
      });
  }

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <div className="min-h-screen bg-primary flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-secondary rounded-xl shadow-2xl p-8 text-center border border-custom">
            {/* Error Icon */}
            <div className="text-red-500 mb-6">
              <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-primary mb-4">
              Bir Hata Oluştu
            </h1>
            
            {/* Error Message */}
            <p className="text-secondary mb-6">
              Uygulama beklenmedik bir hatayla karşılaştı. Bu sorunu çözmek için aşağıdaki seçenekleri deneyebilirsiniz.
            </p>

            {/* Error ID */}
            <div className="mb-6 p-3 bg-tertiary rounded-lg">
              <p className="text-xs text-tertiary mb-1">Hata ID:</p>
              <code className="text-sm font-mono text-primary">{this.state.errorId}</code>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Retry Button */}
              <button
                onClick={this.handleRetry}
                disabled={this.state.retryCount >= 3}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                {this.state.retryCount > 0 ? `Tekrar Dene (${this.state.retryCount}/3)` : 'Tekrar Dene'}
              </button>

              {/* Refresh Button */}
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Sayfayı Yenile
              </button>

              {/* Clear Data Button */}
              <button
                onClick={this.handleClearData}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Tüm Verileri Temizle ve Yenile
              </button>

              {/* Report Error Button */}
              <button
                onClick={this.handleReportError}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Hata Detaylarını Kopyala
              </button>
            </div>

            {/* Development Error Details */}
            {isDevelopment && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm font-medium text-tertiary hover:text-primary">
                  Geliştirici Detayları
                </summary>
                <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="mb-3">
                    <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Hata Mesajı:</h4>
                    <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  
                  {this.state.error.stack && (
                    <div className="mb-3">
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Yığın İzleri:</h4>
                      <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  
                  {this.state.errorInfo && this.state.errorInfo.componentStack && (
                    <div>
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Bileşen Yığını:</h4>
                      <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Too Many Retries Warning */}
            {this.state.tooManyRetries && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Çok fazla deneme yapıldı. Verileri temizlemeyi deneyin.
                </p>
              </div>
            )}

            {/* Help Text */}
            <p className="mt-6 text-xs text-tertiary">
              Sorun devam ediyorsa destek ekibiyle iletişime geçin ve yukarıdaki Hata ID'sini paylaşın.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
